import { useAuth } from 'hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import { routes } from 'constants/routes';
import RewardsLogo from 'assets/RewardsLogo.svg';
import { ReactComponent as BusinessLogo } from 'assets/BusinessLogo.svg';
import { GoSignOut } from 'react-icons/go';
import { PiBuildings, PiQuestion } from 'react-icons/pi';
// Custom ArrowSquareOut icon for external links
const ArrowSquareOutIcon = ({ className }: { className?: string }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M15.75 7.3125C15.75 7.46168 15.6907 7.60476 15.5852 7.71025C15.4798 7.81574 15.3367 7.875 15.1875 7.875C15.0383 7.875 14.8952 7.81574 14.7898 7.71025C14.6843 7.60476 14.625 7.46168 14.625 7.3125V4.17094L9.96117 8.83547C9.85562 8.94102 9.71247 9.00031 9.5632 9.00031C9.41394 9.00031 9.27078 8.94102 9.16523 8.83547C9.05969 8.72992 9.00039 8.58677 9.00039 8.4375C9.00039 8.28823 9.05969 8.14508 9.16523 8.03953L13.8291 3.375H10.6875C10.5383 3.375 10.3952 3.31574 10.2898 3.21025C10.1843 3.10476 10.125 2.96168 10.125 2.8125C10.125 2.66332 10.1843 2.52024 10.2898 2.41475C10.3952 2.30926 10.5383 2.25 10.6875 2.25H15.1875C15.3367 2.25 15.4798 2.30926 15.5852 2.41475C15.6907 2.52024 15.75 2.66332 15.75 2.8125V7.3125ZM12.9375 9C12.7883 9 12.6452 9.05926 12.5398 9.16475C12.4343 9.27024 12.375 9.41332 12.375 9.5625V14.625H3.375V5.625H8.4375C8.58668 5.625 8.72976 5.56574 8.83525 5.46025C8.94074 5.35476 9 5.21168 9 5.0625C9 4.91332 8.94074 4.77024 8.83525 4.66475C8.72976 4.55926 8.58668 4.5 8.4375 4.5H3.375C3.07663 4.5 2.79048 4.61853 2.5795 4.8295C2.36853 5.04048 2.25 5.32663 2.25 5.625V14.625C2.25 14.9234 2.36853 15.2095 2.5795 15.4205C2.79048 15.6315 3.07663 15.75 3.375 15.75H12.375C12.6734 15.75 12.9595 15.6315 13.1705 15.4205C13.3815 15.2095 13.5 14.9234 13.5 14.625V9.5625C13.5 9.41332 13.4407 9.27024 13.3352 9.16475C13.2298 9.05926 13.0867 9 12.9375 9Z"
      fill="currentColor"
    />
  </svg>
);
import { useState } from 'react';
import useIsMobile from 'hooks/useIsMobile';
import Render from 'components/Common/Render';
import { NavigationButton } from 'components/Buttons';

const FAQ_URL = 'https://www.openrewards.com/faq'; // FAQ external link

const LeftNavigation = () => {
  const authContext = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

  const navigationItems = [
    { label: 'Home', path: routes.home, comingSoon: false },
    { label: 'Instant Pay', path: routes.instantPay, comingSoon: false },
    { label: 'List your business', path: routes.listBusiness, comingSoon: false },
    { label: 'Offers', path: routes.offers, comingSoon: true },
    { label: 'Double Up', path: routes.doubleUp, comingSoon: true },
    { label: 'Subscriptions', path: routes.subscriptionTiers, comingSoon: false },
  ];

  const isChooseBusinessPage = location.pathname.includes('/choose-business');

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setIsMobileMenuOpen(false);
      setIsProfileMenuOpen(false);
    }
  };

  const handleLogout = () => {
    authContext?.logout();
    if (isMobile) {
      setIsMobileMenuOpen(false);
      setIsProfileMenuOpen(false);
    }
  };

  if (isMobile) {
    return (
      <>
        {/* Mobile Header with Hamburger, Logo, and Profile */}
        {!isMobileMenuOpen && !isProfileMenuOpen && (
          <div className="fixed top-0 left-0 right-0 h-[60px] bg-white border-b border-borderGrey flex items-center justify-between px-4 z-[60]">
            {/* Hamburger Menu Button */}
            <button
              onClick={() => {
                setIsMobileMenuOpen(true);
                setIsProfileMenuOpen(false);
              }}
              className="w-6 h-6 flex items-center justify-center hover:opacity-80 transition-opacity"
              aria-label="Open menu"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21 12C21 12.1989 20.921 12.3897 20.7803 12.5303C20.6397 12.671 20.4489 12.75 20.25 12.75H3.75C3.55109 12.75 3.36032 12.671 3.21967 12.5303C3.07902 12.3897 3 12.1989 3 12C3 11.8011 3.07902 11.6103 3.21967 11.4697C3.36032 11.329 3.55109 11.25 3.75 11.25H20.25C20.4489 11.25 20.6397 11.329 20.7803 11.4697C20.921 11.6103 21 11.8011 21 12ZM3.75 6.75H20.25C20.4489 6.75 20.6397 6.67098 20.7803 6.53033C20.921 6.38968 21 6.19891 21 6C21 5.80109 20.921 5.61032 20.7803 5.46967C20.6397 5.32902 20.4489 5.25 20.25 5.25H3.75C3.55109 5.25 3.36032 5.32902 3.21967 5.46967C3.07902 5.61032 3 5.80109 3 6C3 6.19891 3.07902 6.38968 3.21967 6.53033C3.36032 6.67098 3.55109 6.75 3.75 6.75ZM20.25 17.25H3.75C3.55109 17.25 3.36032 17.329 3.21967 17.4697C3.07902 17.6103 3 17.8011 3 18C3 18.1989 3.07902 18.3897 3.21967 18.5303C3.36032 18.671 3.55109 18.75 3.75 18.75H20.25C20.4489 18.75 20.6397 18.671 20.7803 18.5303C20.921 18.3897 21 18.1989 21 18C21 17.8011 20.921 17.6103 20.7803 17.4697C20.6397 17.329 20.4489 17.25 20.25 17.25Z"
                  fill="black"
                />
              </svg>
            </button>

            {/* Centered Logo */}
            <div className="flex-1 flex justify-center">
              <img src={RewardsLogo} alt="Open Rewards" className="h-6" />
            </div>

            {/* Profile Icon Button */}
            <button
              onClick={() => {
                setIsProfileMenuOpen(true);
                setIsMobileMenuOpen(false);
              }}
              className="w-8 h-8 rounded-[40px] flex items-center justify-center font-poppins font-semibold text-sm text-indigoPurple bg-lightPurple border border-paleViolet hover:opacity-90 transition-opacity"
              aria-label="Open profile menu"
            >
              {authContext?.user?.name?.charAt(0).toUpperCase() || 'S'}
            </button>
          </div>
        )}

        {/* Left Menu Overlay */}
        {isMobileMenuOpen && (
          <>
            <div
              className="fixed inset-0 bg-black/50 z-[45]"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <div className="fixed top-0 left-0 w-[85%] max-w-[400px] bottom-0 bg-white z-50 overflow-y-auto shadow-xl">
              <div className="flex flex-col h-full">
                {/* Menu Header with Logo and Close Icon */}
                <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 h-[60px] flex-shrink-0">
                  <img src={RewardsLogo} alt="Open Rewards" className="h-6" />
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="w-5 h-5 flex items-center justify-center hover:opacity-80 transition-opacity"
                    aria-label="Close menu"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7.5 2.5V17.5M13.3333 12.5L10.8333 10L13.3333 7.5M4.16667 2.5H15.8333C16.7538 2.5 17.5 3.24619 17.5 4.16667V15.8333C17.5 16.7538 16.7538 17.5 15.8333 17.5H4.16667C3.24619 17.5 2.5 16.7538 2.5 15.8333V4.16667C2.5 3.24619 3.24619 2.5 4.16667 2.5Z"
                        stroke="#0B1215"
                        strokeWidth="1.25"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>

                {/* Navigation Items - Hidden on Choose Business page */}
                <Render when={!isChooseBusinessPage}>
                  {navigationItems.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => handleNavigation(item.path)}
                      className={`w-full text-left px-4 py-4 font-poppins font-normal text-[14px] leading-[100%] transition-colors ${
                        isActivePath(item.path)
                          ? 'text-primary bg-lavenderLight border-l-[5px] border-royalBlue'
                          : 'text-darkBlack hover:bg-gray-50 border-b border-gray-200'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </Render>
              </div>
            </div>
          </>
        )}

        {/* Right Profile Menu Overlay */}
        {isProfileMenuOpen && (
          <>
            <div
              className="fixed inset-0 bg-black/50 z-[45]"
              onClick={() => setIsProfileMenuOpen(false)}
            />
            <div className="fixed top-0 right-0 w-[85%] max-w-[400px] bottom-0 bg-white z-50 overflow-y-auto shadow-xl">
              <div className="flex flex-col h-full">
                {/* Menu Header with Close Icon and Logo */}
                <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 h-[60px] flex-shrink-0">
                  <button
                    onClick={() => setIsProfileMenuOpen(false)}
                    className="w-5 h-5 flex items-center justify-center hover:opacity-80 transition-opacity"
                    aria-label="Close menu"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7.5 2.5V17.5M11.6667 7.5L14.1667 10L11.6667 12.5M4.16667 2.5H15.8333C16.7538 2.5 17.5 3.24619 17.5 4.16667V15.8333C17.5 16.7538 16.7538 17.5 15.8333 17.5H4.16667C3.24619 17.5 2.5 16.7538 2.5 15.8333V4.16667C2.5 3.24619 3.24619 2.5 4.16667 2.5Z"
                        stroke="#0B1215"
                        strokeWidth="1.25"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <img src={RewardsLogo} alt="Open Rewards" className="h-6" />
                </div>

                {/* Business Details */}
                <button
                  onClick={() => handleNavigation(routes.businessDetails)}
                  className={`w-full text-left px-4 py-4 font-poppins font-medium text-[14px] leading-[100%] flex items-center gap-3 transition-colors ${
                    isActivePath(routes.businessDetails)
                      ? 'text-primary bg-lavenderLight border-l-[5px] border-royalBlue'
                      : 'text-darkBlack hover:bg-gray-50 border-b border-gray-200'
                  }`}
                >
                  <PiBuildings className="w-5 h-5" />
                  Business Details
                </button>

                {/* Switch Business */}
                <button
                  onClick={() => handleNavigation(`${routes.chooseBusiness}/1`)}
                  className={`w-full text-left px-4 py-4 font-poppins font-medium text-[14px] leading-[100%] flex items-center gap-3 transition-colors ${
                    location.pathname.includes(routes.chooseBusiness)
                      ? 'text-primary bg-lavenderLight border-l-[5px] border-royalBlue'
                      : 'text-darkBlack hover:bg-gray-50 border-b border-gray-200'
                  }`}
                >
                  <BusinessLogo className="w-5 h-5" />
                  Switch Business
                </button>

                {/* FAQ's */}
                <a
                  href={FAQ_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full text-left px-4 py-4 font-poppins font-medium text-[14px] leading-[100%] flex items-center justify-between text-darkBlack hover:bg-gray-50 transition-colors border-b border-gray-200"
                >
                  <div className="flex items-center gap-3">
                    <PiQuestion className="w-5 h-5" />
                    FAQ's
                  </div>
                  <ArrowSquareOutIcon className="w-[18px] h-[18px] text-darkBlack" />
                </a>

                {/* Log out */}
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-4 font-poppins font-medium text-[14px] leading-[100%] flex items-center gap-3 text-coralRed hover:bg-gray-50 transition-colors border-b border-gray-200"
                >
                  <GoSignOut className="w-5 h-5" />
                  Log out
                </button>

                {/* User Info */}
                <Render when={!!authContext?.user}>
                  <div className="px-4 py-4 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center font-poppins font-semibold text-sm text-white bg-indigo">
                      {authContext?.user?.name?.charAt(0).toUpperCase() || 'S'}
                    </div>
                    <span className="text-darkBlack font-poppins font-medium text-[14px] leading-[100%]">
                      {authContext?.user?.name || 'User'}
                    </span>
                  </div>
                </Render>
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  return (
    <div className="w-[250px] h-screen sticky top-0 flex flex-col justify-between bg-white border-r border-borderGrey">
      {/* Top Section - Logo */}
      <div className="pt-8 pb-6 px-6">
        <button
          onClick={() => handleNavigation(routes.home)}
          className="cursor-pointer hover:opacity-80 transition-opacity"
          aria-label="Go to home"
        >
          <img src={RewardsLogo} alt="Open Rewards" className="h-8" />
        </button>
      </div>

      {/* Middle Section - Navigation Items - Hidden on Choose Business page */}
      <Render when={!isChooseBusinessPage}>
        <div className="flex-1 py-2">
          {navigationItems.map((item) => (
            <NavigationButton
              key={item.path}
              label={item.label}
              onClick={() => handleNavigation(item.path)}
              isActive={isActivePath(item.path)}
              className="mb-1 leading-none pr-4 pl-6"
              icon={
                item.comingSoon ? (
                  <span className="text-[9px] font-semibold px-2 py-1 rounded-full bg-grayLight text-grayDark whitespace-nowrap">
                    Coming Soon
                  </span>
                ) : undefined
              }
              iconPosition="right"
            />
          ))}
        </div>
      </Render>

      {/* Bottom Section - Actions & User */}
      <div>
        {/* Action Buttons - Hidden on Choose Business page */}
        <Render when={!isChooseBusinessPage}>
          <div className="pt-1 border-t border-borderGrey">
            <NavigationButton
              label="Business Details"
              onClick={() => handleNavigation(routes.businessDetails)}
              isActive={isActivePath(routes.businessDetails)}
              icon={<PiBuildings className="w-5 h-5" />}
              className="pl-6 pr-4"
            />

            <NavigationButton
              label="Switch Business"
              onClick={() => handleNavigation(`${routes.chooseBusiness}/1`)}
              isActive={location.pathname.includes(routes.chooseBusiness)}
              icon={<BusinessLogo />}
              className="pl-6 pr-4"
            />

            <a
              href={FAQ_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between w-full py-3 pl-6 pr-4 font-poppins text-sm font-medium text-darkBlack hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <PiQuestion className="w-5 h-5" />
                FAQ's
              </div>
              <ArrowSquareOutIcon className="w-[18px] h-[18px] text-darkBlack" />
            </a>

            <NavigationButton
              label="Log out"
              onClick={handleLogout}
              icon={<GoSignOut />}
              variant="danger"
              className="pl-6 pr-4"
            />
          </div>
        </Render>

        {/* User Info Section - Only show on Choose Business page */}
        <Render when={isChooseBusinessPage && !!authContext?.user}>
          <div className="py-6 border-t border-borderGrey">
            <NavigationButton
              label="Log out"
              onClick={handleLogout}
              icon={<GoSignOut />}
              variant="danger"
              className="pl-6 pr-4"
            />
          </div>
        </Render>

        {/* User Profile */}
        <Render when={!!authContext?.user}>
          <div className="flex items-center gap-3 h-[49px] pl-6 pr-4 py-[14px] mb-2 border-t border-paleGrey">
            <div className="w-5 h-5 rounded-full bg-darkBlue flex items-center justify-center flex-shrink-0 p-1">
              <span className="font-poppins font-medium text-[10px] text-white leading-none">
                {authContext?.user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <span className="font-poppins font-medium text-[14px] leading-[100%] text-darkBlack truncate">
              {authContext?.user?.name || 'User'}
            </span>
          </div>
        </Render>
      </div>
    </div>
  );
};

export default LeftNavigation;
